import React, { useState } from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Container,
  Heading,
  useColorModeValue,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  StatArrow,
  Badge,
  IconButton,
  Tooltip,
  Button
} from '@chakra-ui/react';
import { 
  FiFolder, 
  FiTag, 
  FiCalendar, 
  FiGitBranch, 
  FiArchive,
  FiRefreshCw,
  FiBarChart,
  FiSettings
} from 'react-icons/fi';
import { useAuth } from '../contexts/AuthContext';
import ContentCategorization from '../components/ContentCategorization';
import TagManagement from '../components/TagManagement';
import ContentCollections from '../components/ContentCollections';
import ContentScheduling from '../components/ContentScheduling';
import ContentVersioning from '../components/ContentVersioning';
import ContentArchiving from '../components/ContentArchiving';
import IconWrapper from '../components/IconWrapper';

const ContentManagement: React.FC = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState(0);
  const [selectedContentId, setSelectedContentId] = useState<string | null>(null);

  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');

  const managementStats = [
    {
      label: 'Categories',
      value: '12',
      change: '+2',
      changeType: 'increase' as const,
      icon: FiFolder,
      color: 'blue.400'
    },
    {
      label: 'Tags',
      value: '156',
      change: '+8',
      changeType: 'increase' as const,
      icon: FiTag,
      color: 'purple.400'
    },
    {
      label: 'Collections',
      value: '24',
      change: '+3',
      changeType: 'increase' as const,
      icon: FiFolder,
      color: 'green.400'
    },
    {
      label: 'Scheduled',
      value: '7',
      change: '+1',
      changeType: 'increase' as const,
      icon: FiCalendar,
      color: 'orange.400'
    },
    {
      label: 'Versions',
      value: '89',
      change: '+12',
      changeType: 'increase' as const,
      icon: FiGitBranch,
      color: 'cyan.400'
    },
    {
      label: 'Archived',
      value: '15',
      change: '+2',
      changeType: 'increase' as const,
      icon: FiArchive,
      color: 'red.400'
    }
  ];

  const renderStatsCard = (stat: typeof managementStats[0]) => (
    <Box
      key={stat.label}
      bg={cardBg}
      p={6}
      borderRadius="lg"
      border="1px"
      borderColor={borderColor}
    >
      <Stat>
        <StatLabel>
          <IconWrapper icon={stat.icon} size={16} style={{ marginRight: '8px' }} />
          {stat.label}
        </StatLabel>
        <StatNumber color={stat.color}>
          {stat.value}
        </StatNumber>
        <StatHelpText>
          <StatArrow type={stat.changeType} />
          {stat.change} this week
        </StatHelpText>
      </Stat>
    </Box>
  );

  return (
    <Container maxW="container.xl" py={6}>
      <VStack gap={6} align="stretch">
        {/* Header */}
        <HStack justify="space-between">
          <Heading size="lg" color="white">
            <IconWrapper icon={FiSettings} size={24} style={{ marginRight: '8px' }} />
            Content Management & Organization
          </Heading>
          <HStack gap={2}>
            <Tooltip label="Refresh data">
              <IconButton
                aria-label="Refresh"
                icon={<IconWrapper icon={FiRefreshCw} size={16} />}
                variant="ghost"
                colorScheme="purple"
              />
            </Tooltip>
            <Tooltip label="Analytics">
              <IconButton
                aria-label="Analytics"
                icon={<IconWrapper icon={FiBarChart} size={16} />}
                variant="ghost"
                colorScheme="purple"
              />
            </Tooltip>
          </HStack>
        </HStack>

        {/* Management Statistics */}
        <SimpleGrid columns={{ base: 2, md: 3, lg: 6 }} gap={4}>
          {managementStats.map(renderStatsCard)}
        </SimpleGrid>

        {/* Management Tabs */}
        <Tabs variant="line" colorScheme="purple" onChange={setActiveTab}>
          <TabList>
            <Tab>
              <IconWrapper icon={FiFolder} size={16} style={{ marginRight: '8px' }} />
              Categories
            </Tab>
            <Tab>
              <IconWrapper icon={FiTag} size={16} style={{ marginRight: '8px' }} />
              Tags
            </Tab>
            <Tab>
              <IconWrapper icon={FiFolder} size={16} style={{ marginRight: '8px' }} />
              Collections
            </Tab>
            <Tab>
              <IconWrapper icon={FiCalendar} size={16} style={{ marginRight: '8px' }} />
              Scheduling
            </Tab>
            <Tab>
              <IconWrapper icon={FiGitBranch} size={16} style={{ marginRight: '8px' }} />
              Versioning
            </Tab>
            <Tab>
              <IconWrapper icon={FiArchive} size={16} style={{ marginRight: '8px' }} />
              Archiving
            </Tab>
          </TabList>
          
          <TabPanels>
            <TabPanel px={0}>
              <ContentCategorization showHeader={false} />
            </TabPanel>
            <TabPanel px={0}>
              <TagManagement showHeader={false} />
            </TabPanel>
            <TabPanel px={0}>
              <ContentCollections showHeader={false} userId={user?.uid} />
            </TabPanel>
            <TabPanel px={0}>
              <ContentScheduling showHeader={false} userId={user?.uid} />
            </TabPanel>
            <TabPanel px={0}>
              <ContentVersioning 
                contentId={selectedContentId || ''} 
                showHeader={false} 
              />
            </TabPanel>
            <TabPanel px={0}>
              <ContentArchiving showHeader={false} userId={user?.uid} />
            </TabPanel>
          </TabPanels>
        </Tabs>

        {/* Quick Actions */}
        <Box bg={cardBg} p={6} borderRadius="lg" border="1px" borderColor={borderColor}>
          <VStack align="stretch" gap={4}>
            <Text fontWeight="bold" color="white" fontSize="lg">
              Quick Actions
            </Text>
            <SimpleGrid columns={{ base: 2, md: 4 }} gap={4}>
              <Button
                colorScheme="blue"
                variant="outline"
                leftIcon={<IconWrapper icon={FiFolder} size={16} />}
                onClick={() => setActiveTab(0)}
              >
                Manage Categories
              </Button>
              <Button
                colorScheme="purple"
                variant="outline"
                leftIcon={<IconWrapper icon={FiTag} size={16} />}
                onClick={() => setActiveTab(1)}
              >
                Manage Tags
              </Button>
              <Button
                colorScheme="green"
                variant="outline"
                leftIcon={<IconWrapper icon={FiFolder} size={16} />}
                onClick={() => setActiveTab(2)}
              >
                Create Collection
              </Button>
              <Button
                colorScheme="orange"
                variant="outline"
                leftIcon={<IconWrapper icon={FiCalendar} size={16} />}
                onClick={() => setActiveTab(3)}
              >
                Schedule Content
              </Button>
            </SimpleGrid>
          </VStack>
        </Box>

        {/* Content ID Input for Versioning */}
        {activeTab === 4 && (
          <Box bg={cardBg} p={6} borderRadius="lg" border="1px" borderColor={borderColor}>
            <VStack align="stretch" gap={4}>
              <Text fontWeight="bold" color="white">
                Content Versioning
              </Text>
              <Text color="gray.400" fontSize="sm">
                Enter a content ID to view and manage versions for that specific content item.
              </Text>
              <HStack>
                <input
                  type="text"
                  placeholder="Enter content ID"
                  value={selectedContentId || ''}
                  onChange={(e) => setSelectedContentId(e.target.value)}
                  style={{
                    flex: 1,
                    padding: '8px 12px',
                    backgroundColor: '#374151',
                    border: 'none',
                    borderRadius: '6px',
                    color: 'white',
                    fontSize: '14px'
                  }}
                />
                <Button
                  colorScheme="purple"
                  onClick={() => setActiveTab(4)}
                  isDisabled={!selectedContentId}
                >
                  Load Versions
                </Button>
              </HStack>
            </VStack>
          </Box>
        )}
      </VStack>
    </Container>
  );
};

export default ContentManagement;
